import { CAST_AI } from '@cast/constants';
import {
  StatusChipV2,
  StatusChipV2Color,
  StatusChipV2Props,
  Tooltip,
} from '@cast/design-system';
import { Cluster, ComputedNodeStatus, NodeStatus } from '@cast/types';

import { NODE_STATE_PHASE_LABELS } from 'features/cluster/nodes/constants/labels';

import {
  useCluster,
  isAKSCluster,
  isClusterOnboardingPhase2,
} from '@cast/shared-cluster-context-data-access';

type Props = Omit<StatusChipV2Props, 'label' | 'color'> & {
  status?: NodeStatus | string | ComputedNodeStatus;
  showTooltip?: boolean;
};

const statusToColor = (
  status?: NodeStatus | string | ComputedNodeStatus
): StatusChipV2Color => {
  switch (status) {
    case NodeStatus.ready:
      return 'success';
    case NodeStatus.draining:
      return 'error';
    case NodeStatus.creating:
    case NodeStatus.pending:
    case NodeStatus.deleting:
    case NodeStatus.deleted:
    case NodeStatus.unknown:
      return 'neutral';
    case ComputedNodeStatus.CORDONED:
    case ComputedNodeStatus.DISRUPTED:
    case NodeStatus.interrupted:
    case NodeStatus.notReady:
    case NodeStatus.detached:
    case 'not-ready': // TODO: remove when CORE-2361 is done
      return 'warning';
    default:
      return 'neutral';
  }
};

const getStatusInfo = (
  cluster: Cluster,
  isManaged: boolean,
  status?: NodeStatus | ComputedNodeStatus | string
) => {
  const isAks = isAKSCluster(cluster);
  const clusterIsConnecting = isClusterOnboardingPhase2(
    cluster.status,
    isManaged
  );

  switch (status) {
    case NodeStatus.ready:
    case NodeStatus.creating:
    case NodeStatus.pending:
    case NodeStatus.deleting:
    case NodeStatus.draining:
    case NodeStatus.deleted:
    case NodeStatus.interrupted:
    case NodeStatus.unknown:
    case NodeStatus.detached:
      return {
        label: NODE_STATE_PHASE_LABELS[status],
      };
    case NodeStatus.notReady:
    case 'not-ready': // TODO: remove when CORE-2361 is done
      return {
        tooltip:
          isAks && clusterIsConnecting
            ? `This is a temporary node currently being used to set up ${CAST_AI}. It will be automatically removed once the operation finishes.`
            : undefined,
        label: NODE_STATE_PHASE_LABELS['not-ready'],
      };

    case ComputedNodeStatus.CORDONED:
      return {
        tooltip: 'Scheduling disabled',
        label: NODE_STATE_PHASE_LABELS[status],
      };

    case ComputedNodeStatus.DISRUPTED:
      return {
        tooltip: 'Node is marked for disruption by Karpenter',
        label: NODE_STATE_PHASE_LABELS[status],
      };

    default:
      return {
        label: status,
      };
  }
};

export const NodeStatusChip = ({
  status,
  showTooltip = true,
  size = 'small',
  variant = 'subtle',
  ...props
}: Props) => {
  const { cluster, isManaged } = useCluster();
  const { label, tooltip } = getStatusInfo(cluster, isManaged, status);
  const color = statusToColor(status);

  return (
    <Tooltip title={(showTooltip && tooltip) || ''} placement="top" arrow>
      <StatusChipV2
        label={label}
        color={color}
        size={size}
        variant={variant}
        {...props}
      />
    </Tooltip>
  );
};
